import React, { useMemo } from 'react';
import moment from 'moment';
import { Drawer, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import store from '@redux/store';
import { ApprovalAssetModalStateProps } from '@redux/types/modalType';
import { toggleApprovalAssetModal } from '@redux/actions/modalAction';
import { ApprovalWorkflowItemDto } from '@api/Api';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import {
  QUERY_APPROVAL_APPROVAL_WORKFLOW,
  useGetApprovalWorkflow
} from '@hooks/queries/approval';
import { useAuth } from '@hooks/useAuth';
import AssetModalHead from '@components/Modals/ApprovalModal/ApprovalAssetModal/components/AssetModalHead';
import AssetModalStages from '@components/Modals/ApprovalModal/ApprovalAssetModal/components/AssetModalStages/AssetModalStages';
import ApprovalVersionLockedModal from '@components/MediaViewerHeader/components/ApprovalVersionLockedModal';
import { ArrowLeftWithoutLineIcon } from '@icons';
import './AssetModal.less';

const AssetModalActions = React.lazy(
  () =>
    import(
      '@components/Modals/ApprovalModal/ApprovalAssetModal/components/AssetModalActions'
    )
);
const ApprovalFallbackModal = React.lazy(
  () => import('@components/Modals/ApprovalModal/ApprovalFallbackModal')
);

export default function AssetModal({
  visible,
  assetId,
  versionId,
  approvalWorkflowId,
  openedStageId,
  isShowActions = true,
  callback
}: ApprovalAssetModalStateProps) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { user } = useAuth(true);

  const { data, isLoading: isLoadingApprovalWorkflow } = useGetApprovalWorkflow(
    {
      enabled: !!approvalWorkflowId,
      params: {
        id: approvalWorkflowId || ''
      }
    }
  );

  const approvalWorkflow = data?.approvalWorkflow;
  const asset = useMemo(() => {
    return approvalWorkflow?.assets.find(
      (awAsset) => awAsset.id === assetId && awAsset.versionId === versionId
    );
  }, [assetId, versionId, approvalWorkflow?.assets]);

  const currentStage = useMemo(() => {
    if (!approvalWorkflow?.stages?.length) return null;
    if (!approvalWorkflow.currentStageId) return approvalWorkflow.stages[0];
    return (
      approvalWorkflow.stages.find(
        (item) => item.id === approvalWorkflow.currentStageId
      ) || approvalWorkflow.stages[0]
    );
  }, [approvalWorkflow?.currentStageId, approvalWorkflow?.stages]);

  const isPastDueDateCurrentStage = useMemo(() => {
    if (approvalWorkflow?.isFinished) return false;
    if (approvalWorkflow?.isPaused) return false;
    if (!currentStage?.deadline) return false;
    if (asset?.versionId && approvalWorkflow?.assets?.length) {
      const assetStageStatus = approvalWorkflow.assets
        .find((item) => item.versionId === asset.versionId)
        ?.statuses?.find((item) => item.stageId === currentStage.id)?.status;
      if (
        assetStageStatus &&
        !['pending', 'in_progress'].includes(assetStageStatus)
      )
        return false;
    }
    return moment()
      .startOf('minute')
      .isSameOrAfter(moment(currentStage.deadline).startOf('minute'));
  }, [
    approvalWorkflow?.isFinished,
    approvalWorkflow?.isPaused,
    currentStage?.deadline,
    currentStage?.id,
    approvalWorkflow?.assets,
    asset?.versionId
  ]);

  const queryKey = [
    QUERY_APPROVAL_APPROVAL_WORKFLOW,
    { params: { id: approvalWorkflowId } }
  ];

  const onClose = () => {
    const current = store.getState().modal.approvalAssetModal;
    dispatch(
      toggleApprovalAssetModal(current ? { ...current, visible: false } : null)
    );
  };

  const onUpdateApprovalWorkflow = (
    newApprovalWorkflow: ApprovalWorkflowItemDto
  ) => {
    queryClient.setQueryData(queryKey, {
      approvalWorkflow: newApprovalWorkflow
    });
  };

  const sendMixpanelEvent = () => {
    MixpanelService.track(user.id, MixpanelEventType.MANAGE_ASSETS, {
      addToFavorite: false,
      deleteAsset: false,
      newAsset: false,
      externalLinkShare: false,
      approvalRouting: true,
      newCommentAsset: false,
      annotationLeft: false,
      taskFromComment: false
    });
  };

  if (isLoadingApprovalWorkflow) {
    return (
      <Drawer
        closable
        placement="right"
        headerStyle={{
          padding: '32px 32px 24px',
          border: 'none'
        }}
        closeIcon={null}
        bodyStyle={{
          padding: '0 32px 32px'
        }}
        open={visible}
        onClose={onClose}
        className="approval-asset-modal"
        width={835}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}
        >
          <Spin size="large" />
        </div>
      </Drawer>
    );
  }

  if (!approvalWorkflow) {
    return <ApprovalFallbackModal visible={visible} onClose={onClose} />;
  }

  if (!asset) {
    const assetDifferentVersion = approvalWorkflow?.assets.find(
      (awAsset) => awAsset.id === assetId
    );
    if (assetDifferentVersion) {
      return (
        <ApprovalVersionLockedModal
          visible={visible}
          assetId={assetDifferentVersion.id}
          latestVersionId={assetDifferentVersion.versionId}
          onCancel={() => onClose()}
        />
      );
    }
    return <ApprovalFallbackModal visible={visible} onClose={onClose} />;
  }

  return (
    <Drawer
      closable
      placement="right"
      title={
        asset && (
          <AssetModalHead
            asset={asset}
            approvalWorkflowId={approvalWorkflow.id}
            approvalWorkflowName={approvalWorkflow.name}
            campaignId={approvalWorkflow.campaignId}
            campaignName={approvalWorkflow.campaignName}
            workspaceId={approvalWorkflow.workspaceId}
            isPastDueDateCurrentStage={isPastDueDateCurrentStage}
            onClose={onClose}
          />
        )
      }
      headerStyle={{
        padding: '32px 32px 24px',
        border: 'none'
      }}
      closeIcon={null}
      bodyStyle={{
        padding: '0 32px 32px'
      }}
      open={visible}
      onClose={onClose}
      className="approval-asset-modal"
      width={835}
    >
      {isShowActions && approvalWorkflow.permissions.manage && (
        <AssetModalActions
          asset={asset}
          approvalWorkflow={approvalWorkflow}
          currentStage={currentStage}
          onUpdateApprovalWorkflow={onUpdateApprovalWorkflow}
          onCloseModal={onClose}
          callback={callback}
        />
      )}
      <AssetModalStages
        key={`${asset.id}-${openedStageId}`}
        approvalWorkflow={approvalWorkflow}
        asset={asset}
        openedStageId={openedStageId}
        sendMixpanelEvent={sendMixpanelEvent}
        onUpdateApprovalWorkflow={onUpdateApprovalWorkflow}
      />
      <div onClick={onClose} className="approval-asset-modal-btn-close">
        <ArrowLeftWithoutLineIcon />
      </div>
    </Drawer>
  );
}
